import { AbstractControl } from '@angular/forms';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { countries } from '../components/input-phone-with-county-code/mock-countries';

export const DEFAULT_PHONE_COUNTRY_CODE = '+49';

type PhoneFormValue = {
	country?: string | null;
	phone?: string | number | null;
};

type BirthDateParts = {
	day: number;
	month: number;
	year: number;
};

const PHONE_DIGIT_LIMITS: Record<string, { min: number; max: number }> = {
	'+49': { min: 10, max: 11 }, // Germany
	'+43': { min: 10, max: 11 }, // Austria
	'+41': { min: 9, max: 9 }, // Switzerland
	'+48': { min: 9, max: 9 }, // Poland
	'+44': { min: 10, max: 10 }, // UK
	'+33': { min: 9, max: 9 }, // France
	'+39': { min: 9, max: 10 }, // Italy
	'+34': { min: 9, max: 9 }, // Spain
	'+31': { min: 9, max: 9 }, // Netherlands
	'+1': { min: 10, max: 10 }, // USA/Canada
};

function normalizeDialCode(dialCode?: string | null): string {
	return (dialCode ?? '').replace(/\s+/g, '');
}

function normalizePhoneNumber(phone?: string | null): string {
	return (phone ?? '').replace(/\s+/g, '');
}

export function getPhoneFormValue(phone?: string | null): { country: string; phone: string } {
	const normalizedPhone = normalizePhoneNumber(phone);
	if (!normalizedPhone) {
		return {
			country: DEFAULT_PHONE_COUNTRY_CODE,
			phone: '',
		};
	}

	const sortedCountries = [...countries].sort(
		(left, right) => normalizeDialCode(right.dialCode).length - normalizeDialCode(left.dialCode).length
	);
	const country = sortedCountries.find(item => normalizedPhone.startsWith(`+${normalizeDialCode(item.dialCode)}`));

	if (!country) {
		return {
			country: DEFAULT_PHONE_COUNTRY_CODE,
			phone: normalizedPhone.replace(/^\+/, ''),
		};
	}

	const dialCode = normalizeDialCode(country.dialCode);

	return {
		country: `+${dialCode}`,
		phone: normalizedPhone.slice(dialCode.length + 1),
	};
}

export function isPhoneFormValueValid(value?: PhoneFormValue | null): boolean {
	if (!value?.phone) {
		return true;
	}

	try {
		const country = normalizePhoneNumber(value.country?.toString());
		const phone = normalizePhoneNumber(value.phone?.toString());

		const digitCount = phone.replace(/\D/g, '').length;
		const limits = PHONE_DIGIT_LIMITS[country] ?? { min: 7, max: 15 };
		if (digitCount < limits.min || digitCount > limits.max) {
			return false;
		}

		const phoneNumber = parsePhoneNumberFromString(`${country}${phone}`);
		return !!phoneNumber && phoneNumber.isValid() && phoneNumber.isPossible();
	} catch (_error) {
		return false;
	}
}

export function formatBirthDateForApi(year?: string | number | null, month?: string | number | null, day?: string | number | null): string {
	if (
		year === '' ||
		month === '' ||
		day === '' ||
		year === null ||
		month === null ||
		day === null ||
		year === undefined ||
		month === undefined ||
		day === undefined
	) {
		return '';
	}

	const yearNumber = Number(year);
	const monthNumber = Number(month);
	const dayNumber = Number(day);

	if (!Number.isInteger(yearNumber) || !Number.isInteger(monthNumber) || !Number.isInteger(dayNumber)) {
		return '';
	}

	if (yearNumber > new Date().getFullYear()) {
		return '';
	}

	return new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber)).toISOString();
}

export function getBirthDateParts(birthDate?: string | null): BirthDateParts | null {
	if (!birthDate) {
		return null;
	}

	const parsedDate = new Date(birthDate);
	if (Number.isNaN(parsedDate.getTime())) {
		return null;
	}

	return {
		day: parsedDate.getUTCDate(),
		month: parsedDate.getUTCMonth() + 1,
		year: parsedDate.getUTCFullYear(),
	};
}

export function normalizePhoneCountryCode(country?: string | null): string {
	const dialCode = normalizeDialCode(country);
	return dialCode ? `+${dialCode.replace(/^\+/, '')}` : DEFAULT_PHONE_COUNTRY_CODE;
}

export function phoneValidatorForLibrary(control: AbstractControl) {
	if (!control.value?.phone) {
		return null;
	}
	try {
		const phoneNumber = parsePhoneNumberFromString(control?.value?.country + control?.value?.phone.toString());
		if (phoneNumber.isValid() && phoneNumber.isPossible()) {
			return null;
		} else {
			return { invalidPhone: true };
		}
	} catch (e) {
		return { invalidCountryCode: true };
	}
}

export function phoneValidatorForGermany(control: AbstractControl) {
	if (!control.value?.phone) {
		return null;
	}

	const phoneNumber = control.value.country + control.value.phone.toString();

	if (phoneNumber.startsWith('00')) {
		return { invalidPhone: 'Invalid format: use "+" instead of "00"' };
	}

	if (phoneNumber.startsWith('+490') || phoneNumber.startsWith('+430')) {
		return { invalidPhone: 'Phone number should not start with +490' };
	}
	const digitOnlyRegex = /^\+49\d+$/;
	const digitOnlyRegex2 = /^\+43\d+$/;
	if (phoneNumber.startsWith('+49')) {
		if (!digitOnlyRegex.test(phoneNumber)) {
			return { invalidPhone: 'Phone number must contain digits only.' };
		}
	}
	if (phoneNumber.startsWith('+43')) {
		if (!digitOnlyRegex2.test(phoneNumber)) {
			return { invalidPhone: 'Phone number must contain digits only.' };
		}
	}
	if (phoneNumber.length > 15) {
		return { invalidPhone: 'Phone number cannot exceed 15 digits.' };
	}

	if (phoneNumber.length < 12) {
		return { invalidPhone: 'Phone number must have at least 9 digits after +49.' };
	}

	return null;
}
